import * as Sentry from '@sentry/react';

// --- Sentry PII scrubbing for the overlay (unauthenticated, OBS-rendered surface) ---
// The overlay reads two distinct secrets from the URL query string:
// `token` (WS auth, useWsEvents.ts) and `livekit` (LiveKit access JWT, StreamView.tsx).
// Sentry's default httpContextIntegration attaches event.request.url to every event
// regardless of sendDefaultPii, so both params must be redacted from every URL-bearing
// surface beforeSend/beforeBreadcrumb can see. This is a generalized superset of web's
// `redactUrlToken` (packages/web/src/main.tsx), which only matches `token=`.
const SENSITIVE_KEY_PATTERN = /token|key|secret/i;

function redactSensitiveUrlParams(value: string): string {
  return value.replace(/([?&](?:token|livekit)=)[^&#\s]*/gi, '$1[REDACTED]');
}

function scrubSensitiveKeys(bag: Record<string, unknown> | undefined): void {
  if (!bag || typeof bag !== 'object') return;
  for (const name of Object.keys(bag)) {
    if (SENSITIVE_KEY_PATTERN.test(name)) {
      bag[name] = '[REDACTED]';
    }
  }
}

function scrubBreadcrumbData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  if (typeof data.url === 'string') data.url = redactSensitiveUrlParams(data.url);
  scrubSensitiveKeys(data);
}

export function scrubOverlayEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  try {
    // 1. Strip token/livekit from the recorded request URL.
    if (event.request && typeof event.request.url === 'string') {
      event.request.url = redactSensitiveUrlParams(event.request.url);
    }

    // 2. Strip a Referer header, if present — it can carry a prior page's full URL
    // (including ?token=/?livekit=) even though OBS normally loads with no referrer.
    const headers = event.request?.headers as Record<string, string> | undefined;
    if (headers && typeof headers === 'object') {
      for (const name of Object.keys(headers)) {
        if (name.toLowerCase() === 'referer') {
          // `name` is drawn from `Object.keys(headers)`, so this key lookup always hits.
          const value = headers[name];
          if (value !== undefined) {
            headers[name] = redactSensitiveUrlParams(value);
          }
        }
      }
    }

    // 3. Strip token/key/secret-named fields from extra.
    scrubSensitiveKeys(event.extra as Record<string, unknown> | undefined);

    // 4. Scrub breadcrumbs already buffered on the event (fetch/XHR URLs + data).
    if (Array.isArray(event.breadcrumbs)) {
      for (const bc of event.breadcrumbs) {
        scrubBreadcrumbData(bc.data as Record<string, unknown> | undefined);
      }
    }

    // 5. Strip token/livekit from exception messages that may embed a URL.
    const values = event.exception?.values;
    if (Array.isArray(values)) {
      for (const v of values) {
        if (typeof v.value === 'string') v.value = redactSensitiveUrlParams(v.value);
      }
    }

    // 6. Strip token/livekit from a top-level message string.
    if (typeof event.message === 'string') {
      event.message = redactSensitiveUrlParams(event.message);
    }
  } catch {
    // Never let scrubbing throw — a failed scrub must not drop the event.
  }
  return event;
}

export function scrubOverlayBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  try {
    scrubBreadcrumbData(breadcrumb.data as Record<string, unknown> | undefined);
    if (typeof breadcrumb.message === 'string') {
      breadcrumb.message = redactSensitiveUrlParams(breadcrumb.message);
    }
  } catch {
    // Never let scrubbing throw.
  }
  return breadcrumb;
}

if (import.meta.env.VITE_ENABLE_SENTRY === 'true') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: false,
    // NOTE: an empty array merges with Sentry's default integrations, it does NOT
    // disable them — the default global error/rejection handlers stay active. Never
    // set defaultIntegrations: false. No replay/browser-tracing here: overlay has no
    // auth/session and no router (App.tsx does manual pathname branching).
    integrations: [],
    tracesSampleRate: 0,
    // Production overlay origin, confirmed live (packages/web's VITE_OVERLAY_URL
    // points here). Preview/local origins intentionally do NOT match — an
    // empty/undefined array is allow-ALL, not block-all, so this is the
    // deliberately narrowed production-only allowlist per the plan's design.
    allowUrls: [/^https:\/\/overlay\.sleeved\.gg/],
    beforeSend: scrubOverlayEvent,
    beforeBreadcrumb: scrubOverlayBreadcrumb,
  });
}
